import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Calendar,
  Brain,
  Sparkles,
  Loader2,
  ArrowRight,
  Check,
  AlertCircle
} from "lucide-react";
import { FaMeta, FaMicrosoft, FaLinkedin } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Count-up from `from` to `to` with an ease-out curve — used for the impact reveal.
function AnimatedNumber({ from = 0, to = 0, decimals = 2, prefix = "", suffix = "", duration = 1000 }) {
  const [val, setVal] = useState(from);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const animate = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [from, to, duration]);
  return <span>{prefix}{val.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</span>;
}

const PLATFORM_ICONS = {
  meta: <FaMeta className="w-4 h-4 text-[#1877F2]" />,
  google: <FcGoogle className="w-4 h-4" />,
  bing: <FaMicrosoft className="w-4 h-4 text-[#00A4EF]" />,
  linkedin: <FaLinkedin className="w-4 h-4 text-[#0A66C2]" />,
};

export default function OptimizationHistory({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAIReallocation, setShowAIReallocation] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [customBudget, setCustomBudget] = useState("");
  const [applying, setApplying] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/optimization/logs`, {
        credentials: "include",
      });
      if (response.ok) {
        setLogs(await response.json());
      }
    } catch (error) {
      toast.error("Failed to load optimization history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleGetRecommendations = async () => {
    setAiLoading(true);
    try {
      const budget = customBudget ? parseFloat(customBudget) : null;
      const response = await fetch(`${API}/optimization/ai-reallocation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          total_daily_budget: budget,
          apply_changes: false
        })
      });

      if (response.ok) {
        const data = await response.json();
        setRecommendations(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to get AI recommendations");
      }
    } catch (error) {
      toast.error("Failed to get AI recommendations");
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplyRecommendations = async () => {
    setApplying(true);
    try {
      const budget = customBudget ? parseFloat(customBudget) : null;
      const response = await fetch(`${API}/optimization/ai-reallocation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          total_daily_budget: budget,
          apply_changes: true
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Applied budget changes to ${data.applied_count} campaigns!`);
        setShowAIReallocation(false);
        setRecommendations(null);
        fetchLogs(); // Refresh logs
      } else {
        toast.error("Failed to apply changes");
      }
    } catch (error) {
      toast.error("Failed to apply changes");
    } finally {
      setApplying(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div data-testid="optimization-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl text-white font-bold mb-2">
              Optimization History
            </h1>
            <p className="text-zinc-400">
              View all automatic budget adjustments and run AI reallocation
            </p>
          </div>
          <Button
            onClick={() => setShowAIReallocation(true)}
            className="bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-700 hover:to-purple-700"
          >
            <Brain className="w-4 h-4 mr-2" />
            AI Budget Reallocation
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <span className="text-xs uppercase tracking-widest text-zinc-400">Total Optimizations</span>
            <p className="font-heading text-3xl text-white font-bold mt-2">
              {logs.length}
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <span className="text-xs uppercase tracking-widest text-zinc-400">Budget Increased</span>
            <p className="font-heading text-3xl text-emerald-400 font-bold mt-2">
              {logs.filter(l => l.action === "budget_increased").length}
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <span className="text-xs uppercase tracking-widest text-zinc-400">Budget Decreased</span>
            <p className="font-heading text-3xl text-red-400 font-bold mt-2">
              {logs.filter(l => l.action === "budget_decreased").length}
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <span className="text-xs uppercase tracking-widest text-zinc-400">AI Reallocations</span>
            <p className="font-heading text-3xl text-violet-400 font-bold mt-2">
              {logs.filter(l => l.action === "ai_reallocation").length}
            </p>
          </motion.div>
        </div>

        {/* Logs */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Optimization Log</CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="py-12 text-center">
                <TrendingUp className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400">No optimizations yet</p>
                <p className="text-zinc-500 text-sm mt-1">
                  Run the optimizer or AI reallocation to see results here
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {logs.map((log, index) => (
                  <motion.div
                    key={log.log_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="flex flex-col md:flex-row md:items-center justify-between p-4 rounded-xl bg-white/5 border border-white/5"
                  >
                    <div className="flex items-start gap-4">
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center",
                        log.action === "budget_increased" 
                          ? "bg-emerald-500/10" 
                          : log.action === "ai_reallocation"
                            ? "bg-violet-500/10"
                            : "bg-red-500/10"
                      )}>
                        {log.action === "budget_increased" ? (
                          <TrendingUp className="w-5 h-5 text-emerald-400" />
                        ) : log.action === "ai_reallocation" ? (
                          <Brain className="w-5 h-5 text-violet-400" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-red-400" />
                        )}
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {PLATFORM_ICONS[log.platform] || <DollarSign className="w-4 h-4 text-zinc-400" />}
                          <span className="text-white font-medium">{log.campaign_name}</span>
                          <Badge 
                            className={cn(
                              "ml-2",
                              log.action === "budget_increased"
                                ? "bg-emerald-500/10 text-emerald-400"
                                : log.action === "ai_reallocation"
                                  ? "bg-violet-500/10 text-violet-400"
                                  : "bg-red-500/10 text-red-400"
                            )}
                          >
                            {log.action === "budget_increased" ? "Increased" : log.action === "ai_reallocation" ? "AI Reallocation" : "Decreased"}
                          </Badge>
                        </div>
                        <p className="text-zinc-400 text-sm">{log.reason}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <div className="flex items-center gap-2 text-sm">
                            <DollarSign className="w-4 h-4 text-zinc-500" />
                            <span className="text-zinc-400">
                              {formatCurrency(log.old_budget)} → {formatCurrency(log.new_budget)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-zinc-500">ROAS:</span>
                            <span className={cn(
                              "font-medium",
                              log.roas_before >= 1.75 ? "text-emerald-400" : "text-red-400"
                            )}>
                              {log.roas_before?.toFixed(2)}x
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 md:mt-0 md:text-right">
                      <div className="flex items-center gap-2 text-zinc-400 text-sm">
                        <Calendar className="w-4 h-4" />
                        {formatDate(log.created_at)}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AI Reallocation Dialog */}
      <Dialog open={showAIReallocation} onOpenChange={setShowAIReallocation}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Brain className="w-5 h-5 text-violet-400" />
              AI Budget Reallocation
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Let AI analyze your campaign performance and recommend optimal budget distribution
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Budget Input */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Total Daily Budget (Optional)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Leave empty to use current total"
                  value={customBudget}
                  onChange={(e) => setCustomBudget(e.target.value)}
                  className="bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                />
                <Button
                  onClick={handleGetRecommendations}
                  disabled={aiLoading}
                  className="bg-violet-600 text-white hover:bg-violet-700 whitespace-nowrap"
                >
                  {aiLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Analyze
                </Button>
              </div>
              <p className="text-xs text-zinc-500">
                Specify a budget or let AI redistribute your current total across campaigns
              </p>
            </div>

            {/* Recommendations */}
            <AnimatePresence>
              {recommendations && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4"
                >
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <p className="text-zinc-400 text-xs">Total Budget</p>
                      <p className="text-white font-bold text-lg">
                        ${recommendations.total_budget?.toFixed(0)}/day
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <p className="text-zinc-400 text-xs">Campaigns</p>
                      <p className="text-white font-bold text-lg">
                        {recommendations.campaign_count}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <p className="text-zinc-400 text-xs">Changes</p>
                      <p className="text-white font-bold text-lg">
                        {recommendations.recommendations?.filter(r => Math.abs(r.budget_change) > 0.01).length}
                      </p>
                    </div>
                  </div>

                  {/* Projected Impact (ROAS ↑ / CAC ↓) */}
                  {recommendations.projection && (
                    <div data-testid="ai-reallocation-projection" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-xs uppercase tracking-widest text-zinc-400">Blended ROAS</span>
                          <TrendingUp className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div className="flex items-baseline gap-2 mt-2">
                          <span className="text-zinc-500 text-lg line-through">{recommendations.projection.before.roas.toFixed(2)}x</span>
                          <ArrowRight className="w-4 h-4 text-zinc-500 self-center" />
                          <span data-testid="ai-projection-roas-after" className="text-emerald-400 font-heading text-3xl font-bold">
                            <AnimatedNumber from={recommendations.projection.before.roas} to={recommendations.projection.after.roas} decimals={2} suffix="x" />
                          </span>
                        </div>
                        {recommendations.projection.deltas.roas_pct != null && (
                          <p className="text-emerald-400 text-sm mt-1 font-medium">
                            {recommendations.projection.deltas.roas_pct >= 0 ? "▲" : "▼"} {Math.abs(recommendations.projection.deltas.roas_pct)}% return on ad spend
                          </p>
                        )}
                      </div>
                      <div className="rounded-xl border border-blue-500/25 bg-blue-500/5 p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-xs uppercase tracking-widest text-zinc-400">Cost per Acquisition</span>
                          <TrendingDown className="w-4 h-4 text-blue-400" />
                        </div>
                        <div className="flex items-baseline gap-2 mt-2">
                          <span className="text-zinc-500 text-lg line-through">
                            {recommendations.projection.before.cac != null ? `$${recommendations.projection.before.cac.toFixed(2)}` : "—"}
                          </span>
                          <ArrowRight className="w-4 h-4 text-zinc-500 self-center" />
                          <span data-testid="ai-projection-cac-after" className="text-blue-400 font-heading text-3xl font-bold">
                            {recommendations.projection.after.cac != null ? (
                              <AnimatedNumber from={recommendations.projection.before.cac ?? recommendations.projection.after.cac} to={recommendations.projection.after.cac} decimals={2} prefix="$" />
                            ) : "—"}
                          </span>
                        </div>
                        {recommendations.projection.deltas.cac_pct != null && (
                          <p className="text-blue-400 text-sm mt-1 font-medium">
                            {recommendations.projection.deltas.cac_pct <= 0 ? "▼" : "▲"} {Math.abs(recommendations.projection.deltas.cac_pct)}% cost per customer
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recommendation List */}
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                    {recommendations.recommendations?.map((rec, index) => (
                      <motion.div
                        key={rec.campaign_id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={cn(
                          "p-3 rounded-lg border",
                          rec.budget_change > 0 
                            ? "bg-emerald-500/5 border-emerald-500/20" 
                            : rec.budget_change < 0
                              ? "bg-red-500/5 border-red-500/20"
                              : "bg-white/5 border-white/10"
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {PLATFORM_ICONS[rec.platform]}
                            <span className="text-white font-medium text-sm truncate max-w-[200px]">
                              {rec.name}
                            </span>
                            <Badge className="bg-white/10 text-zinc-400 text-xs">
                              Score: {rec.score}
                            </Badge>
                          </div>
                          <div className={cn(
                            "text-sm font-semibold",
                            rec.budget_change > 0 ? "text-emerald-400" : rec.budget_change < 0 ? "text-red-400" : "text-zinc-400"
                          )}>
                            {rec.change_percent > 0 ? "+" : ""}{rec.change_percent}%
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 text-zinc-400">
                            <span>${rec.current_budget.toFixed(0)}</span>
                            <ArrowRight className="w-3 h-3" />
                            <span className={cn(
                              "font-medium",
                              rec.budget_change > 0 ? "text-emerald-400" : rec.budget_change < 0 ? "text-red-400" : "text-white"
                            )}>
                              ${rec.recommended_budget.toFixed(0)}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-zinc-400">
                            <span>ROAS: {rec.metrics.roas}x</span>
                            <span>CTR: {rec.metrics.ctr}%</span>
                          </div>
                        </div>
                        
                        <p className="text-xs text-zinc-400 mt-1">{rec.reason}</p>
                      </motion.div>
                    ))}
                  </div>

                  {/* Info Banner */}
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-amber-300 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Applying changes will update all campaign budgets immediately
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowAIReallocation(false);
                setRecommendations(null);
              }}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            {recommendations && (
              <Button
                onClick={handleApplyRecommendations}
                disabled={applying}
                className="bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-700 hover:to-purple-700"
              >
                {applying ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Apply All Changes
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
